import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import ProductService from '../services/product.service';
import CategoryService from '../services/category.service';
import { useCart } from '../context/CartContext';
import { Plus, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { formatPrice } from '../utils/currency';

const Home = () => {
    // Product State for the grid
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    // Category State
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('all');

    // Carousel State
    const [featuredProducts, setFeaturedProducts] = useState([]);
    const [currentSlide, setCurrentSlide] = useState(0);

    const { addToCart } = useCart();

    // Initial Load: Categories and Featured Products
    useEffect(() => {
        fetchInitialData();
    }, []);

    // Fetch Products when Category Selection Changes
    useEffect(() => {
        fetchProductsByCategory();
    }, [selectedCategory]);

    // Auto-advance carousel
    useEffect(() => {
        if (featuredProducts.length > 1) {
            const timer = setInterval(() => {
                setCurrentSlide((prev) => (prev + 1) % featuredProducts.length);
            }, 6000);
            return () => clearInterval(timer);
        }
    }, [featuredProducts.length]);

    const fetchInitialData = async () => {
        try {
            const [catRes, prodRes] = await Promise.all([
                CategoryService.getCategories(),
                ProductService.getProducts() // Fetch all initially to find features? Or fetch featured endpoint?
                // For now, assuming we filter featured from all locally for the carousel, or backend has ?featured=true
                // User requirement: Carousel shows features. Grid shows category filtered.
            ]);

            setCategories(catRes.data.results || []);

            // Setup featured products
            // Ideally backend has ?is_featured=true. If not, we filter client side from initial fetch
            // But if we switch to server side filtering for the grid, we need to keep featured separate.
            const allProducts = prodRes.data.results || [];
            const featured = allProducts.filter(p => p.is_featured);
            setFeaturedProducts(featured.length > 0 ? featured : []);

        } catch (error) {
            console.error(error);
        }
    };

    const fetchProductsByCategory = async () => {
        setLoading(true);
        try {
            // Server-side filtering logic
            const params = {};
            if (selectedCategory !== 'all') {
                params.category = selectedCategory; // backend expects ID usually
            }

            // If the user wants to filter by brand too, we'd add brand parameter here.
            // Current UI only has category tabs.

            const response = await ProductService.getProducts(params);
            setProducts(response.data.results || response.data || []);
        } catch (error) {
            console.error("Failed to fetch products", error);
            setProducts([]);
        } finally {
            setLoading(false);
        }
    };

    const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % featuredProducts.length);
    const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + featuredProducts.length) % featuredProducts.length);

    return (
        <div className="bg-white pb-20">
            {/* Hero Carousel */}
            {featuredProducts.length > 0 ? (
                <div className="relative h-[600px] w-full overflow-hidden bg-gray-100">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={currentSlide}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.8 }}
                            className="absolute inset-0"
                        >
                            {/* Image Handling */}
                            <img
                                src={featuredProducts[currentSlide].primary_image || (featuredProducts[currentSlide].images?.[0]?.image_url)}
                                alt={featuredProducts[currentSlide].name}
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-black/30" />

                            <div className="absolute inset-0 flex items-center justify-center text-center text-white p-4">
                                <div className="max-w-4xl space-y-6">
                                    {/* 1. Brand */}
                                    <motion.div
                                        initial={{ y: 20, opacity: 0 }}
                                        animate={{ y: 0, opacity: 1 }}
                                        transition={{ delay: 0.3 }}
                                        className="text-sm tracking-[0.3em] uppercase font-bold text-white/80"
                                    >
                                        {featuredProducts[currentSlide].brand_name || featuredProducts[currentSlide].brand?.name || 'SEEKER'}
                                    </motion.div>

                                    {/* 2. Product Name */}
                                    <motion.h1
                                        initial={{ y: 20, opacity: 0 }}
                                        animate={{ y: 0, opacity: 1 }}
                                        transition={{ delay: 0.5 }}
                                        className="font-serif text-5xl md:text-7xl"
                                    >
                                        {featuredProducts[currentSlide].name}
                                    </motion.h1>

                                    {/* 3. Category */}
                                    <motion.p
                                        initial={{ y: 20, opacity: 0 }}
                                        animate={{ y: 0, opacity: 1 }}
                                        transition={{ delay: 0.7 }}
                                        className="text-lg font-light text-white/90 uppercase tracking-widest"
                                    >
                                        {featuredProducts[currentSlide].category_name || featuredProducts[currentSlide].category?.name}
                                    </motion.p>

                                    {/* 4. View (Static) */}
                                    <motion.div
                                        initial={{ y: 20, opacity: 0 }}
                                        animate={{ y: 0, opacity: 1 }}
                                        transition={{ delay: 0.9 }}
                                    >
                                        <Link to={`/product/${featuredProducts[currentSlide].id}`}>
                                            <Button className="bg-white text-black hover:bg-white/90 border-none mt-4 min-w-[150px] tracking-widest font-bold text-xs py-4">
                                                VIEW
                                            </Button>
                                        </Link>
                                    </motion.div>
                                </div>
                            </div>
                        </motion.div>
                    </AnimatePresence>

                    {/* Carousel Controls (only if multiple) */}
                    {featuredProducts.length > 1 && (
                        <>
                            <button onClick={prevSlide} className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-white/20 backdrop-blur-md text-white rounded-full hover:bg-white/40 transition-colors">
                                <ChevronLeft size={24} />
                            </button>
                            <button onClick={nextSlide} className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-white/20 backdrop-blur-md text-white rounded-full hover:bg-white/40 transition-colors">
                                <ChevronRight size={24} />
                            </button>
                        </>
                    )}
                </div>
            ) : (
                <div className="relative h-[200px] w-full bg-gray-50 flex items-center justify-center">
                    <div className="text-center">
                        <h1 className="font-serif text-2xl mb-2 text-gray-400">Featured Products</h1>
                        <p className="text-xs text-gray-400 uppercase tracking-widest">No featured products found.</p>
                    </div>
                </div>
            )}

            {/* Featured Categories for Server-Side Filtering */}
            <div className="w-full px-4 md:px-8 py-12 flex flex-wrap gap-4 border-b border-gray-100 justify-center">
                <Button
                    variant={selectedCategory === 'all' ? 'primary' : 'secondary'}
                    onClick={() => setSelectedCategory('all')}
                    className="whitespace-nowrap min-w-[100px]"
                >
                    All Items
                </Button>
                {categories.map(cat => (
                    <Button
                        key={cat.id}
                        variant={selectedCategory === cat.id ? 'primary' : 'secondary'}
                        onClick={() => setSelectedCategory(cat.id)}
                        className="whitespace-nowrap min-w-[100px]"
                    >
                        {cat.name}
                    </Button>
                ))}
            </div>

            {/* Product Grid - Full Width */}
            <section className="w-full px-4 md:px-8 py-8">
                <div className="flex items-center justify-between mb-8">
                    <h2 className="font-serif text-3xl">Must Haves</h2>
                </div>

                {loading ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
                        {[...Array(5)].map((_, i) => (
                            <div key={i} className="aspect-[3/4] bg-gray-100 animate-pulse rounded-sm" />
                        ))}
                    </div>
                ) : products.length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
                        {products.map((product) => (
                            <div key={product.id} className="group relative">
                                <Link to={`/product/${product.id}`}>
                                    <div className="aspect-[3/4] bg-gray-100 overflow-hidden rounded-sm relative mb-4">
                                        {product.primary_image || (product.images && product.images[0]) ? (
                                            <img
                                                src={product.primary_image || product.images[0]?.image_url}
                                                alt={product.name}
                                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-gray-300 bg-gray-50 text-xs uppercase tracking-widest">No Image</div>
                                        )}

                                        <button
                                            onClick={(e) => {
                                                e.preventDefault();
                                                const variantId = product.variants?.[0]?.id;
                                                if (variantId) addToCart(variantId);
                                            }}
                                            className="absolute bottom-4 right-4 w-10 h-10 bg-white rounded-full flex items-center justify-center translate-y-4 opacity-0 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300 shadow-lg hover:bg-black hover:text-white"
                                        >
                                            <Plus size={20} />
                                        </button>

                                        {product.is_new && (
                                            <span className="absolute top-2 left-2 bg-white px-2 py-1 text-[10px] uppercase tracking-widest font-bold">New</span>
                                        )}
                                    </div>
                                    <div className="space-y-1">
                                        <h3 className="font-serif text-base leading-tight group-hover:underline underline-offset-4 decoration-1">{product.name}</h3>
                                        <p className="text-sm font-medium text-gray-900 font-sans">{formatPrice(product.base_price)}</p>
                                    </div>
                                </Link>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="py-20 text-center">
                        <p className="text-gray-400 font-serif text-xl">No products found in this category.</p>
                        <button onClick={() => setSelectedCategory('all')} className="text-sm border-b border-black mt-4 pb-1">Show All Items</button>
                    </div>
                )}
            </section>
        </div>
    );
};

export default Home;
